import { Building2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AssignPlanModal } from '../components/AssignPlanModal';
import { TenantsTable } from '../components/TenantsTable';
import { ToggleTenantStatusModal } from '../components/ToggleTenantStatusModal';
import { useTenants } from '../hooks/useTenants';
import type { AdminTenantListDto, TenantStatus } from '../types/tenants.types';
import { privatePaths } from '@/app/router/paths/privatePaths';
import { useTerritoriesByLevel } from '@/features/countries/hooks/useTerritoriesByLevel';
import { TerritorialLevel } from '@/features/countries/types/countries.types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ahkinia/Select/select';
import { SectionHeader } from '@/shared/components/ui/header';
import { Input } from '@/shared/components/ui/input';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

const STATUS_OPTIONS: { value: TenantStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'Active', label: 'Activo' },
  { value: 'Trial', label: 'Prueba' },
  { value: 'TrialGracePeriod', label: 'Período de gracia' },
  { value: 'PendingSubscription', label: 'Pendiente' },
  { value: 'PastDue', label: 'Vencido' },
  { value: 'Suspended', label: 'Suspendido' },
  { value: 'Expired', label: 'Expirado' },
];

export default function TenantsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState<TenantStatus | 'all'>('all');
  const [countryId, setCountryId] = useState<string | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');

  const { territories: allTerritories, isLoading: countriesLoading } = useTerritoriesByLevel(
    TerritorialLevel.Pais,
  );
  const allCountries = useMemo(
    () => [...allTerritories].sort((a, b) => a.commonName.localeCompare(b.commonName)),
    [allTerritories],
  );

  const [selectedTenant, setSelectedTenant] = useState<AdminTenantListDto | null>(null);
  const [toggleAction, setToggleAction] = useState<'suspend' | 'reactivate' | null>(null);
  const [assignTenant, setAssignTenant] = useState<AdminTenantListDto | null>(null);

  const hasFilters =
    status !== 'all' ||
    countryId !== 'all' ||
    searchTerm !== '' ||
    createdFrom !== '' ||
    createdTo !== '';

  const { tenants, pagination } = useTenants({
    page,
    pageSize,
    status: status !== 'all' ? status : null,
    countryId: countryId !== 'all' ? countryId : null,
    searchTerm: searchTerm || null,
    createdFrom: createdFrom || null,
    createdTo: createdTo || null,
  });

  const hasData = tenants.length > 0;

  const handleStatusChange = (value: string) => {
    setStatus(value as TenantStatus | 'all');
    setPage(1);
  };

  const handleCountryChange = (value: string) => {
    setCountryId(value);
    setPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleCreatedFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreatedFrom(e.target.value);
    setPage(1);
  };

  const handleCreatedToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreatedTo(e.target.value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setStatus('all');
    setCountryId('all');
    setSearchTerm('');
    setCreatedFrom('');
    setCreatedTo('');
    setPage(1);
  };

  const handleViewDetail = (tenant: AdminTenantListDto) => {
    navigate(privatePaths.TENANTS.detail.replace(':id', tenant.id), { state: { tenant } });
  };

  const handleToggleStatus = (tenant: AdminTenantListDto) => {
    setSelectedTenant(tenant);
    setToggleAction(tenant.status === 'Suspended' ? 'reactivate' : 'suspend');
  };

  const handleToggleClose = () => {
    setSelectedTenant(null);
    setToggleAction(null);
  };

  return (
    <div data-testid="tenants-page" className="flex flex-col gap-6 p-6">
      <SectionHeader
        sectionIcon={<Building2 />}
        title="Espacios de trabajo"
        subtitle="Gestión de espacios de trabajo del sistema"
      />

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3">
        {/* Búsqueda */}
        <div className="flex flex-col gap-1 min-w-48">
          <span className="text-primary text-xs">Buscar</span>
          <Input
            data-testid="filter-search-input"
            placeholder="Nombre"
            value={searchTerm}
            onChange={handleSearchChange}
            className="h-9"
          />
        </div>

        {/* Estado */}
        <div className="flex flex-col gap-1 min-w-45">
          <span className="text-primary text-xs">Estado</span>
          <Select value={status} onValueChange={handleStatusChange}>
            <SelectTrigger className="h-9" data-testid="filter-status-select">
              <SelectValue placeholder="Todos los estados" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* País */}
        <div className="flex flex-col gap-1">
          <span className="text-primary text-xs">País</span>
          <Select value={countryId} onValueChange={handleCountryChange} disabled={countriesLoading}>
            <SelectTrigger className="w-48 h-9" data-testid="filter-country-select">
              <SelectValue placeholder="Todos los países" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los países</SelectItem>
              {allCountries.map((country) => (
                <SelectItem key={country.id} value={country.id}>
                  {country.commonName} ({country.isoCode})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Fecha desde */}
        <div className="flex flex-col gap-1">
          <span className="text-primary text-xs">Creado desde</span>
          <Input
            data-testid="filter-created-from"
            type="date"
            value={createdFrom}
            onChange={handleCreatedFromChange}
            className="h-9"
          />
        </div>

        {/* Fecha hasta */}
        <div className="flex flex-col gap-1">
          <span className="text-primary text-xs">Creado hasta</span>
          <Input
            data-testid="filter-created-to"
            type="date"
            value={createdTo}
            onChange={handleCreatedToChange}
            className="h-9"
          />
        </div>

        {/* Limpiar filtros */}
        {hasFilters && (
          <UnifiedButton
            data-testid="clear-filters-btn"
            variant="outline"
            onClick={handleClearFilters}
            className="h-9 self-end"
          >
            <X size={16} />
            Limpiar
          </UnifiedButton>
        )}
      </div>

      {hasData ? (
        <TenantsTable
          data={tenants}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          onViewDetail={handleViewDetail}
          onToggleStatus={handleToggleStatus}
          onAssignPlan={setAssignTenant}
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-12 rounded-xl bg-background border-outline">
          <Building2 size={48} className="text-primary mb-4 opacity-50" />
          <p className="text-primary mb-4">
            {hasFilters
              ? 'Sin resultados para los filtros aplicados.'
              : 'No hay espacios de trabajo disponibles.'}
          </p>
        </div>
      )}

      {assignTenant && (
        <AssignPlanModal
          tenantId={assignTenant.id}
          tenantName={assignTenant.name}
          open
          onClose={() => setAssignTenant(null)}
        />
      )}

      {selectedTenant && toggleAction && (
        <ToggleTenantStatusModal
          tenant={selectedTenant}
          action={toggleAction}
          onClose={handleToggleClose}
        />
      )}
    </div>
  );
}
